import { useId, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Card, Modal } from '@safetyhub/ui';
import { adminApi } from '../lib/api';
import { ModalCancelButton } from '../components/ModalCancelButton';
import { isAdminPermissionKey, type AdminPermissionKey } from '../lib/adminPermissionKeys';

type Role = {
  id: string;
  code: string;
  name: string;
  description: string | null;
  isSystem: boolean;
  permissions: { permissionKey: string }[];
  _count?: { admins: number };
};

/**
 * 권한 키 → 화면 라벨. `Record<AdminPermissionKey, …>`라 키를 더하고 라벨을 안 달면 admin tsc가 red다 —
 * 키 사본이 서버 정본과 같은지는 `__tests__/adminPermissionKeys.guard.test.ts`가 잰다.
 */
const PERMISSION_LABELS: Record<AdminPermissionKey, string> = {
  'dashboard': '대시보드',
  'organization-management': '조직/사용자 관리',
  'user-management': '사용자 관리',
  'feedback': '피드백 관리',
  'impersonation': '사용자 빙의',
  'ai-settings': 'AI 설정',
  'sms-monitoring': 'SMS 모니터링',
  'system-settings': '시스템 설정',
  'admin-management': '관리자 관리',
  // 아래 셋의 문구 출처 — 키를 신설한 마이그레이션 `20260408040000_add_monitoring_view_permission`의 머리 「운영 모니터링
  // 권한 키」 · 그 키가 거는 메뉴 라벨(`components/Layout.tsx`의 「보고서 양식 빌더」·「법정 의무 카탈로그」)
  'monitoring-view': '운영 모니터링',
  'report-builder': '보고서 양식 빌더',
  'legal-obligation-catalog': '법정 의무 카탈로그',
};

/** 서버가 준 키의 라벨 — 정본 밖 키(사본보다 서버가 앞선 배포 창)는 키 문자열을 그대로 보인다 */
function permissionLabel(key: string): string {
  return isAdminPermissionKey(key) ? PERMISSION_LABELS[key] : key;
}

export default function Roles() {
  const queryClient = useQueryClient();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editTarget, setEditTarget] = useState<Role | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Role | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'roles'],
    queryFn: () => adminApi.getRoles(),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => adminApi.deleteRole(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'roles'] });
      setDeleteTarget(null);
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">로딩 중...</div>
      </div>
    );
  }

  if (error || !data?.success) {
    return (
      <div className="bg-red-50 p-4 rounded text-red-600">
        데이터를 불러오는데 실패했습니다.
      </div>
    );
  }

  const roles = data.data || [];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">관리자 역할 관리</h2>
        <Button variant="primary" onClick={() => setShowCreateModal(true)}>
          <span>+</span>
          <span>역할 추가</span>
        </Button>
      </div>

      <Card padding="none" radius="lg" className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                역할 코드
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                역할 이름
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                설명
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                권한 수
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                관리자 수
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {roles.map((role) => (
              <tr
                key={role.id}
                className="cursor-pointer hover:bg-gray-50"
                onClick={() => setEditTarget(role)}
              >
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm text-gray-700">{role.code}</span>
                    {role.isSystem && (
                      <span className="px-2 py-0.5 text-xs bg-brand-100 text-brand-700 rounded">
                        시스템
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">
                  {role.name}
                </td>
                <td className="px-6 py-4 text-gray-500 text-sm max-w-xs truncate">
                  {role.description || '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-gray-500">
                  {role.permissions.length}개
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-gray-500">
                  {role._count?.admins ?? 0}명
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <p className="mt-4 text-sm text-gray-500">총 {roles.length}개 역할</p>

      {showCreateModal && (
        <RoleModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'roles'] });
            setShowCreateModal(false);
          }}
        />
      )}

      {editTarget && (
        <RoleModal
          role={editTarget}
          onClose={() => setEditTarget(null)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'roles'] });
            setEditTarget(null);
          }}
          onDelete={(r) => { setEditTarget(null); setDeleteTarget(r); }}
        />
      )}

      {deleteTarget && (
        <Modal
          open
          onClose={() => setDeleteTarget(null)}
          title="역할 삭제"
          size="md"
          footer={
            <>
              <button
                onClick={() => setDeleteTarget(null)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                취소
              </button>
              <Button
                variant="danger"
                onClick={() => deleteMutation.mutate(deleteTarget.id)}
                disabled={deleteMutation.isPending}
              >
                {deleteMutation.isPending ? '처리 중...' : '삭제'}
              </Button>
            </>
          }
        >
            {(deleteTarget._count?.admins ?? 0) > 0 && (
              <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded text-yellow-700 text-sm">
                이 역할에는 {deleteTarget._count?.admins}명의 관리자가 할당되어 있습니다. 삭제하면 해당 관리자의 역할이 제거됩니다.
              </div>
            )}
            <p className="text-gray-600">
              <span className="font-medium">{deleteTarget.name}</span> 역할을 삭제하시겠습니까?
            </p>
            {deleteMutation.isError && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded text-red-600 text-sm">
                역할 삭제에 실패했습니다.
              </div>
            )}
        </Modal>
      )}
    </div>
  );
}

// export는 미저장 닫기 spec(`__tests__/formModalDirtyClose.test.tsx`)이 이 모달만 띄우기 위함이다.
export function RoleModal({
  role,
  onClose,
  onSuccess,
  onDelete,
}: {
  role?: Role;
  onClose: () => void;
  onSuccess: () => void;
  onDelete?: (role: Role) => void;
}) {
  const isEdit = !!role;
  const isReadonly = !!role?.isSystem;
  const [code, setCode] = useState(role?.code ?? '');
  const [name, setName] = useState(role?.name ?? '');
  const [description, setDescription] = useState(role?.description ?? '');
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(
    new Set(role?.permissions.map((p) => p.permissionKey) ?? [])
  );
  const [error, setError] = useState('');
  const formId = useId();

  const { data: keysData } = useQuery({
    queryKey: ['admin', 'permission-keys'],
    queryFn: () => adminApi.getPermissionKeys(),
  });

  const permissionKeys = keysData?.data ?? [];

  const mutation = useMutation({
    mutationFn: () => {
      if (isEdit) {
        return adminApi.updateRole(role.id, {
          name,
          description: description || undefined,
          permissionKeys: Array.from(selectedKeys),
        });
      }
      return adminApi.createRole({
        code,
        name,
        description: description || undefined,
        permissionKeys: Array.from(selectedKeys),
      });
    },
    onSuccess: (result) => {
      if (result.success) {
        onSuccess();
      } else {
        setError(result.error?.message || '저장에 실패했습니다.');
      }
    },
    onError: () => {
      setError('저장 중 오류가 발생했습니다.');
    },
  });

  const toggleKey = (key: string) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    mutation.mutate();
  };

  // 미저장 닫기 보호 — 기준선은 열림 시점의 role 값(생성이면 빈 폼). 시스템 역할(읽기 전용)은 입력이 잠겨 늘 false.
  // 권한 집합은 정렬한 문자열로 비교한다(크기 비교를 쓰지 않는 것은 `selectAllMembershipGuard` 래칫의 축이라서다).
  const keysSignature = (keys: Iterable<string>) => Array.from(keys).sort().join('\n');
  const [initialKeysSignature] = useState(() => keysSignature(role?.permissions.map((p) => p.permissionKey) ?? []));
  const isDirty =
    !isReadonly &&
    (code !== (role?.code ?? '') ||
      name !== (role?.name ?? '') ||
      description !== (role?.description ?? '') ||
      keysSignature(selectedKeys) !== initialKeysSignature);

  return (
    // 부모가 열 때만 마운트하므로 open은 늘 true다. 제출 버튼이 base footer(폼 밖)에 있어 `form` 속성으로 묶는다.
    <Modal
      open
      onClose={onClose}
      title={isReadonly ? '역할 상세' : isEdit ? '역할 수정' : '역할 추가'}
      size="lg"
      isDirty={isDirty}
      footer={
        <div className="flex w-full justify-between">
          <div>
            {isEdit && !isReadonly && onDelete && role && (
              <button
                type="button"
                onClick={() => { onClose(); onDelete(role); }}
                className="px-4 py-2 text-red-600 hover:text-red-800 text-sm"
              >
                삭제
              </button>
            )}
          </div>
          <div className="flex gap-3">
            <ModalCancelButton onCancel={onClose} label={isReadonly ? '닫기' : '취소'} />
            {!isReadonly && (
              <Button type="submit" form={formId} variant="primary" disabled={mutation.isPending}>
                {mutation.isPending ? '처리 중...' : isEdit ? '수정' : '추가'}
              </Button>
            )}
          </div>
        </div>
      }
    >
        <form id={formId} onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded text-red-600 text-sm">
              {error}
            </div>
          )}

          {!isEdit && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                역할 코드 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                required
                placeholder="예: CONTENT_MANAGER"
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-500 font-mono"
              />
              <p className="mt-1 text-xs text-gray-500">영문 대문자, 숫자, 언더스코어만 사용</p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              역할 이름 <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              disabled={isReadonly}
              placeholder="예: 콘텐츠 관리자"
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-500 disabled:bg-gray-100"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              설명
            </label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="역할에 대한 설명"
              disabled={isReadonly}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-500 disabled:bg-gray-100"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              권한
            </label>
            <div className="border border-gray-200 rounded p-3 space-y-2 max-h-56 overflow-y-auto">
              {permissionKeys.length === 0 ? (
                <p className="text-sm text-gray-400">권한 목록을 불러오는 중...</p>
              ) : (
                permissionKeys.map((key) => (
                  <label key={key} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isReadonly || selectedKeys.has(key)}
                      onChange={() => toggleKey(key)}
                      disabled={isReadonly}
                      className="rounded text-red-600 focus:ring-red-500 disabled:opacity-50"
                    />
                    <span className="text-sm text-gray-700">
                      {permissionLabel(key)}
                    </span>
                    <span className="text-xs text-gray-400 font-mono">({key})</span>
                  </label>
                ))
              )}
            </div>
            <p className="mt-1 text-xs text-gray-500">{selectedKeys.size}개 선택됨</p>
          </div>
        </form>
    </Modal>
  );
}
